import { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { postRequest, getRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Chip,
  Grid
} from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, title = 'Add Customer' }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddClick = (customer) => {
    onCustomerAdd(customer);
    setTimeout(() => {
      onHide();
    }, 100);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleAddClick(customer)}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

const EditConveyancingTransferOfEquity = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  console.log('🔄 EditConveyancingTransferOfEquity Component Loading...');
  console.log('🔄 Lead ID:', lead_id);

  const [customersToAdd, setCustomersToAdd] = useState([]);
  const [customersToRemove, setCustomersToRemove] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedLenderPanelId, setSelectedLenderPanelId] = useState('');

  // Info checkboxes state - 3 fields for Transfer of Equity
  const [infoOptions, setInfoOptions] = useState({
    isMortgaged: false,
    requiresLocalSearchInsurance: false,
    firstTimeBuyer: false
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      address1: '',
      address2: '',
      town: '',
      county: '',
      postcode: '',
      propertyValue: '',
      transferType: 'whole',
      considerationValue: '',
      ownershipType: 'Freehold'
    },
  });

  const handleCustomerAddToAdd = (customer) => {
    console.log('➕ Adding customer to Add list:', customer);
    if (!customersToAdd.some((c) => c._id === customer._id)) {
      setCustomersToAdd([...customersToAdd, customer]);
      setCustomerError(false);
    }
  };

  const handleCustomerAddToRemove = (customer) => {
    console.log('➕ Adding customer to Remove list:', customer);
    if (!customersToRemove.some((c) => c._id === customer._id)) {
      setCustomersToRemove([...customersToRemove, customer]);
    }
  };

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      console.log('=== AUTO-FILL DEBUG START (TRANSFER OF EQUITY) ===');
      console.log('Lead ID:', lead_id);

      // Try to get Transfer of Equity specific data
      const res = await getRequest(`private/getEditDataTransferOfEquity/${lead_id}`, authCtx.token);
      console.log('🔍 API Response:', res);

      if (res?.status === 200 && res?.data) {
        const lead = res.data;
        console.log('✅ TRANSFER OF EQUITY DATA LOADED:', lead);
        await performAutoFill(lead);
      } else {
        console.log('⚠️ No specific Transfer of Equity data, trying generic lead data');
        // Fallback to generic lead data
        const genericRes = await postRequest(`private/leadList`, { lead_id: lead_id }, authCtx.token);
        if (genericRes?.data && Array.isArray(genericRes.data) && genericRes.data.length > 0) {
          await performAutoFill(genericRes.data[0]);
        } else {
          Swal.fire('Error', 'Could not load lead data for editing', 'error');
        }
      }
    } catch (err) {
      console.error('❌ AUTO-FILL ERROR:', err);
      Swal.fire('Error', 'Failed to load lead data: ' + err.message, 'error');
    } finally {
      setLoading(false);
      console.log('=== AUTO-FILL DEBUG END (TRANSFER OF EQUITY) ===');
    }
  };

  const performAutoFill = async (lead) => {
    console.log('🚀 PERFORMING TRANSFER OF EQUITY AUTO-FILL WITH DATA:', lead);

    try {
      // Property Address Auto-fill - check address or property_address first
      const addressSource = lead.address || lead.property_address;
      console.log('📍 Address source:', addressSource);

      if (addressSource) {
        if (typeof addressSource === 'string') {
          const parts = addressSource.split(',').map(p => p.trim());
          setValue('address1', parts[0] || '');
          setValue('address2', parts[1] || '');
          setValue('town', parts[2] || '');
          setValue('county', parts[3] || '');
          setValue('postcode', parts[parts.length - 1] || '');
        } else if (typeof addressSource === 'object') {
          setValue('address1', addressSource.address1 || addressSource.line1 || '');
          setValue('address2', addressSource.address2 || addressSource.line2 || '');
          setValue('town', addressSource.town || '');
          setValue('county', addressSource.county || addressSource.country || '');
          setValue('postcode', addressSource.postcode || '');
        }
      } else if (lead.address1 || lead.postcode) {
        // Fallback to root level fields
        setValue('address1', lead.address1 || '');
        setValue('address2', lead.address2 || '');
        setValue('town', lead.town || '');
        setValue('county', lead.country || lead.county || '');
        setValue('postcode', lead.postcode || '');
      }

      // Property Value
      const propertyValue = lead.property_value || lead.net_value || '';
      setValue('propertyValue', String(propertyValue));

      // Transfer Type
      const transferType = lead.transfer_type || lead.transferType || 'whole';
      setValue('transferType', transferType);

      // Consideration Value
      const considerationValue = lead.consideration_value || lead.considerationValue || '';
      setValue('considerationValue', String(considerationValue));

      // Ownership Type
      const ownership = lead.ownership_type || (lead.leasehold ? 'Leasehold' : 'Freehold');
      setValue('ownershipType', ownership);

      // Partner Auto-fill
      if (lead.partner_id) {
        const partnerId = typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id;
        setSelectedPartnerId(partnerId);
        console.log('  🤝 Partner ID:', partnerId);
      }

      // Lender Panel Auto-fill
      if (lead.lender_panel_id) {
        const lenderPanelId = typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id;
        setSelectedLenderPanelId(lenderPanelId);
        console.log('  🏦 Lender Panel ID:', lenderPanelId);
      }

      // Customers to Add
      if (lead.customers_to_add && Array.isArray(lead.customers_to_add)) {
        setCustomersToAdd(lead.customers_to_add);
      } else if (lead.customersToAdd && Array.isArray(lead.customersToAdd)) {
        setCustomersToAdd(lead.customersToAdd);
      }

      // Customers to Remove
      if (lead.customers_to_remove && Array.isArray(lead.customers_to_remove)) {
        setCustomersToRemove(lead.customers_to_remove);
      } else if (lead.customersToRemove && Array.isArray(lead.customersToRemove)) {
        setCustomersToRemove(lead.customersToRemove);
      }

      // Info Options
      const infoData = {
        isMortgaged: Boolean(lead.is_mortgaged || lead.isMortgaged),
        requiresLocalSearchInsurance: Boolean(lead.requires_local_search_insurance || lead.requiresLocalSearchInsurance),
        firstTimeBuyer: Boolean(lead.is_first_time_buyer || lead.firstTimeBuyer)
      };

      setInfoOptions(infoData);

      // Force form update
      setTimeout(() => {
        const formValues = {
          address1: lead.address1 || lead.property_address?.address1 || '',
          address2: lead.address2 || lead.property_address?.address2 || '',
          town: lead.town || lead.property_address?.town || '',
          county: lead.country || lead.property_address?.county || '',
          postcode: lead.postcode || lead.property_address?.postcode || '',
          propertyValue: String(propertyValue),
          transferType: transferType,
          considerationValue: String(considerationValue),
          ownershipType: ownership
        };
        reset(formValues);
      }, 500);

      console.log('🎉 TRANSFER OF EQUITY AUTO-FILL COMPLETED');
    } catch (err) {
      console.error('❌ ERROR IN PERFORM AUTO-FILL:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
        console.log('✅ Partners loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching partners:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/lenderPanelList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setLenderPanels(res.data.docs);
        console.log('✅ Lender panels loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching lender panels:', err);
    }
  };

  useEffect(() => {
    if (lead_id) {
      fetchLeadDetails();
    }
    fetchPartners();
    fetchLenderPanels();
  }, [lead_id]);

  const onSubmit = async (formData) => {
    if (customersToAdd.length === 0 && customersToRemove.length === 0) {
      setCustomerError(true);
      Swal.fire('Error', 'Please add at least one customer to add or remove', 'error');
      return;
    }

    const payload = {
      // Property details
      property_address: {
        address1: formData.address1,
        address2: formData.address2,
        town: formData.town,
        country: formData.county,
        postcode: formData.postcode
      },
      property_value: parseFloat(formData.propertyValue),
      transfer_type: formData.transferType,
      consideration_value: parseFloat(formData.considerationValue),
      ownership_type: formData.ownershipType,
      service_type: 'Transfer_of_Equity',

      // Customers
      customers_to_add: customersToAdd.map((c) => c._id),
      customers_to_remove: customersToRemove.map((c) => c._id),

      // Info options
      is_mortgaged: infoOptions.isMortgaged,
      requires_local_search_insurance: infoOptions.requiresLocalSearchInsurance,
      is_first_time_buyer: infoOptions.firstTimeBuyer,

      // Partner and Lender Panel
      partner_id: selectedPartnerId || undefined,
      lender_panel_id: selectedLenderPanelId || undefined,

      // Meta
      created_id: localStorage.getItem('id'),
    };

    try {
      const res = await postRequest(`private/saveConveyancingTransferOfEquity/${lead_id}`, payload, authCtx.token);
      if (res?.status === 200 || res?.status === 'success') {
        Swal.fire('Success', 'Transfer of Equity details updated successfully!', 'success');
        navigate('/leads');
      } else {
        Swal.fire('Error', res?.message || 'Update failed', 'error');
      }
    } catch (err) {
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  const handleInfoToggle = (key) => {
    setInfoOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Transfer of Equity', path: '' },
        ]}
      />
      <br />
      <br />

      <div className="page-header pb-3 mb-3">
        <h1 className="mt-0 mb-0" style={{ fontSize: '1.5rem', fontWeight: '500' }}>
          Edit Conveyancing - Transfer of Equity #{lead_id}
        </h1>
      </div>

      <div style={{ backgroundColor: '#e9ecef', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
        <strong>Transfer of Equity Details</strong>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="card mb-4" style={{ backgroundColor: '#ffffff' }}>
          <div className="card-body">
            {/* Property Section */}
            <h5 className="mb-4">Property</h5>
            <Row>
              <Col md={6}>
                <h6 className="mb-3">Address</h6>
                <Controller
                  name="address1"
                  control={control}
                  rules={{ required: 'Address is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Address Line 1"
                      fullWidth
                      sx={{ mb: 2 }}
                      error={!!errors.address1}
                      helperText={errors.address1?.message}
                    />
                  )}
                />
                <Controller
                  name="address2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Address Line 2"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="town"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Town/City"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="county"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="County/State"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="postcode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Postcode"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Col>

              <Col md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <h6>Property Value</h6>
                    <Controller
                      name="propertyValue"
                      control={control}
                      rules={{ required: 'Property value is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.propertyValue}
                          helperText={errors.propertyValue?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Transfer - Whole or Part?</h6>
                    <Controller
                      name="transferType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select {...field} displayEmpty>
                            <MenuItem value="whole">Whole</MenuItem>
                            <MenuItem value="part">Part</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Consideration Value</h6>
                    <Controller
                      name="considerationValue"
                      control={control}
                      rules={{ required: 'Consideration value is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.considerationValue}
                          helperText={errors.considerationValue?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Ownership Type</h6>
                    <Controller
                      name="ownershipType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select {...field} displayEmpty>
                            <MenuItem value="Freehold">Freehold</MenuItem>
                            <MenuItem value="Leasehold">Leasehold</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Partner</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedPartnerId}
                        onChange={(e) => setSelectedPartnerId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Partner</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Lender Panel</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedLenderPanelId}
                        onChange={(e) => setSelectedLenderPanelId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel</em>
                        </MenuItem>
                        {lenderPanels.map((lender) => (
                          <MenuItem key={lender._id} value={lender._id}>
                            {lender.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Col>
            </Row>

            <hr className="my-4" />

            {/* Customers Section */}
            <Row>
              <Col md={6}>
                <h5 className="mb-3">Customers to Add</h5>
                {customerError && customersToAdd.length === 0 && (
                  <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                    Please add at least one customer.
                  </Alert>
                )}
                <div className="mb-3">
                  {customersToAdd.length > 0 ? (
                    customersToAdd.map((customer, index) => (
                      <div key={customer._id || index} className="d-flex align-items-center mb-2">
                        <span className="me-3">
                          {customer.title} {customer.forename} {customer.surname}
                        </span>
                        <span className="me-3 text-muted">{customer.email}</span>
                        <CloseIcon
                          style={{ cursor: 'pointer', color: '#dc3545' }}
                          onClick={() => {
                            setCustomersToAdd(prev => prev.filter(p => p._id !== customer._id));
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '10px' }}>
                      No customers added yet
                    </div>
                  )}
                  <Button
                    variant="text"
                    onClick={() => setShowAddModal(true)}
                    style={{ color: '#6f42c1' }}
                  >
                    + ADD CUSTOMER
                  </Button>
                </div>
              </Col>

              <Col md={6}>
                <h5 className="mb-3">Customers to Remove</h5>
                <div className="mb-3">
                  {customersToRemove.length > 0 ? (
                    customersToRemove.map((customer, index) => (
                      <div key={customer._id || index} className="d-flex align-items-center mb-2">
                        <span className="me-3">
                          {customer.title} {customer.forename} {customer.surname}
                        </span>
                        <span className="me-3 text-muted">{customer.email}</span>
                        <CloseIcon
                          style={{ cursor: 'pointer', color: '#dc3545' }}
                          onClick={() => {
                            setCustomersToRemove(prev => prev.filter(p => p._id !== customer._id));
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '10px' }}>
                      No customers to remove
                    </div>
                  )}
                  <Button
                    variant="text"
                    onClick={() => setShowRemoveModal(true)}
                    style={{ color: '#6f42c1' }}
                  >
                    + ADD CUSTOMER TO REMOVE
                  </Button>
                </div>
              </Col>
            </Row>

            <hr className="my-4" />

            {/* Info Section - 3 fields for Transfer of Equity */}
            <h5 className="mb-3">Info</h5>
            <div className="d-flex flex-wrap gap-2">
              {Object.entries({
                isMortgaged: 'Is Mortgaged',
                requiresLocalSearchInsurance: 'Requires Local Search Insurance',
                firstTimeBuyer: 'Is First Time Buyer'
              }).map(([key, label]) => (
                <Chip
                  key={key}
                  label={label}
                  variant={infoOptions[key] ? "filled" : "outlined"}
                  color={infoOptions[key] ? "primary" : "default"}
                  onClick={() => handleInfoToggle(key)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
        </div>

        <Row className="mb-4">
          <Col>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                type="submit"
                style={{
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  textTransform: 'none',
                  padding: '10px 30px'
                }}
              >
                Update Transfer of Equity Details
              </Button>
            </Stack>
          </Col>
        </Row>
      </Form>

      {/* Add Customer Modal */}
      <CustomerSearchModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleCustomerAddToAdd}
        title="Add Customer"
      />

      {/* Remove Customer Modal */}
      <CustomerSearchModal
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleCustomerAddToRemove}
        title="Add Customer to Remove"
      />
    </>
  );
};

export default EditConveyancingTransferOfEquity;
